/**
 * Model žádosti o vystoupení z ČSK
 */

import * as context from "../../../context";
import * as state from "../../../lib/state";
import * as formatting from "../../../lib/formatting";
import * as forms from "../../../lib/forms";
import * as webRequest from "../../shared/web-request/web-request";
import * as apiList from "../../../modules/system/code-list/api-code-list";
import * as api from "../../../lib/api";

interface State {
	reasons: apiList.CodeList[];
}

/**
 * Definice dat formuláře
 */
export interface FormFields {
	date: string;
	reason_for_termination?: apiList.CodeList;
}

/**
 * Definice formuláře
 */
const formOptions: forms.FormOptions<FormFields> = {
	fields: {
		date: {
			title: "ke dni (nelze provést zpětně)",
			defaultValue: "",
			required: true,
			validate: value => value.trim().length === 0 ? "Datum je povinné pole" : formatting.setDateToZero(new Date()) > new Date(value) ? "Nelze provést zpětně" : ""
		},
		reason_for_termination: {
			title: "Důvod",
			defaultValue: undefined,
			required: true,
			validate: value => !value ? "Důvod je povinné pole" : ""
		},
	}
};

/**
 * Model stránky
 */
export class Model implements state.StateModel {
	private stateContainer: state.StateContainer<State>;
	public form: forms.Form<FormFields>;
	public common: webRequest.Model;

	constructor(private context: context.StateContext) {
		this.stateContainer = new state.StateContainer<State>({
			reasons: []
		}, context);
		this.common = new webRequest.Model(context);
		this.form = new forms.Form<FormFields>(formOptions, context);
	}

	/**
	 * Vrací kolekci stavových kontejnerů
	 */
	getStateContainers = () => {
		return [
			this.stateContainer,
			...this.common.getStateContainers(),
			...this.form.getStateContainers()
		];
	}

	getReasons = () => {
		return this.stateContainer.get().reasons;
	}

	/**
	 * Potvrdí formulář uživatelem
	 */
	confirm = async () => {
		await this.form.validate();
		if (this.form.isValid()) {
			const data = this.form.getFields();
			await this.common.send(data, "zadost-o-vystoupeni-z-CSK");
		}
		else {
			await this.context.standardDialogs.openInformationDialog("Proběhla kontrola formuláře. Opravte prosím označené chyby a akci opakujte.");
		}
	}

	loadReasons = async () => {
		const reasons = await this.context.api.post("/code-list/reason_for_terminations", {}) as any;
		const data = reasons.data;
		data.unshift({ id: api.emptyGuid, name: "", code: "" })
		await this.stateContainer.merge(_ => ({
			reasons: reasons.data,
		}));
	}

	/**
	 * Načte data do formuláře 
	 */
	loadData = async (webRequestId: string) => {
		await this.common.loadData(webRequestId);
		await this.loadReasons();
		const webRequest = this.common.getWebRequest();
		if (webRequest) {
			const data = webRequest.data as FormFields;
			await this.form.setFields(data);
		}
	}

	/**
	 * Provede reset formuláře
	 */
	reset = async () => {
		await Promise.all([
			this.form.clearFields(),
			this.common.reset(),
			await this.loadReasons()
		]);
	}
}